import React, { useState, useMemo } from 'react';
import { Heading } from '../catalyst/heading';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../catalyst/table';
import { Text } from '../catalyst/text';
import { Badge } from '../catalyst/badge';
import { Select } from '../catalyst/select';
import { Input } from '../catalyst/input';
import { Button } from '../catalyst/button';
import { Field, Label } from '../catalyst/fieldset';
import StatCard from '../components/StatCard';
import { useSupabaseQuery } from '../hooks/useSupabaseQuery';
import type { LedgerTransaction, BenefitCurrency, LedgerEntryType } from '../types/database';
import { Wallet, ArrowDownUp, Search } from 'lucide-react';
import ResourceLink from '../components/ResourceLink';

const PAGE_SIZE = 25;

type LedgerRow = LedgerTransaction & {
	users: { display_name: string | null; phone_number: string } | null;
};

const ENTRY_TYPE_LABELS: Record<LedgerEntryType, string> = {
	GRANT: 'Grant',
	SPEND: 'Spend',
	DEPOSIT: 'Deposit',
	WITHDRAWAL: 'Withdrawal',
};

const CURRENCY_LABELS: Record<BenefitCurrency, string> = {
	credit: 'Credit',
	cash: 'Cash',
	product: 'Product',
};

function formatAmount(amount: number, currency: BenefitCurrency): string {
	if (currency === 'product') {
		return `${amount > 0 ? '+' : ''}${amount}`;
	}
	const euros = Math.abs(amount) / 100;
	const sign = amount < 0 ? '' : '+';
	return `${sign}€${euros.toFixed(2)}`;
}

function formatDate(dateStr: string): string {
	return new Date(dateStr).toLocaleDateString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	});
}

const LedgerPage: React.FC = () => {
	const [filterType, setFilterType] = useState('');
	const [filterCurrency, setFilterCurrency] = useState('');
	const [search, setSearch] = useState('');
	const [offset, setOffset] = useState(0);

	// Global balances: sum all ledger transactions grouped by currency
	const { data: allTransactions, loading: balancesLoading } = useSupabaseQuery<{ currency: BenefitCurrency; amount: number }>(
		(client) =>
			client
				.from('ledger_transactions')
				.select('currency, amount'),
		[]
	);

	const globalBalances = useMemo(() => {
		const map = new Map<BenefitCurrency, { total: number; count: number }>();
		for (const row of allTransactions) {
			const existing = map.get(row.currency) || { total: 0, count: 0 };
			existing.total += Number(row.amount);
			existing.count += 1;
			map.set(row.currency, existing);
		}
		return Array.from(map.entries()).map(([currency, { total, count }]) => ({
			currency,
			total,
			count,
		}));
	}, [allTransactions]);

	const totalTransactionCount = allTransactions.length;

	// Paginated + filtered transaction query
	const { data: transactions, loading, error, count } = useSupabaseQuery<LedgerRow>(
		(client) => {
			let q = client
				.from('ledger_transactions')
				.select('*, users(display_name, phone_number)', { count: 'exact' })
				.order('created_at', { ascending: false })
				.range(offset, offset + PAGE_SIZE - 1);
			if (filterType) q = q.eq('entry_type', filterType);
			if (filterCurrency) q = q.eq('currency', filterCurrency);
			if (search) q = q.or(`user_id.ilike.%${search}%,code_id.ilike.%${search}%,order_id.ilike.%${search}%,reason.ilike.%${search}%`);
			return q;
		},
		[filterType, filterCurrency, search, offset]
	);

	const totalPages = count != null ? Math.ceil(count / PAGE_SIZE) : 0;
	const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

	return (
		<div className="p-6">
			<Heading level={1}>Ledger</Heading>

			{/* Global Balance Cards */}
			<div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
				<StatCard
					title="Total Transactions"
					value={balancesLoading ? '…' : totalTransactionCount}
					icon={<ArrowDownUp className="size-5" />}
				/>
				{globalBalances.map(({ currency, total, count: txCount }) => (
					<StatCard
						key={currency}
						title={`${CURRENCY_LABELS[currency]} Balance`}
						value={
							currency === 'product'
								? total
								: `€${(total / 100).toFixed(2)}`
						}
						subtitle={`${txCount} transaction${txCount !== 1 ? 's' : ''}`}
						icon={<Wallet className="size-5" />}
					/>
				))}
			</div>

			{/* Filters */}
			<div className="mt-6 flex flex-wrap items-end gap-4">
				<Field className="w-48">
					<Label>Type</Label>
					<Select
						value={filterType}
						onChange={(e: React.ChangeEvent<HTMLSelectElement>) => { setFilterType(e.target.value); setOffset(0); }}
					>
						<option value="">All Types</option>
						<option value="GRANT">Grant</option>
						<option value="SPEND">Spend</option>
						<option value="DEPOSIT">Deposit</option>
						<option value="WITHDRAWAL">Withdrawal</option>
					</Select>
				</Field>
				<Field className="w-48">
					<Label>Currency</Label>
					<Select
						value={filterCurrency}
						onChange={(e: React.ChangeEvent<HTMLSelectElement>) => { setFilterCurrency(e.target.value); setOffset(0); }}
					>
						<option value="">All Currencies</option>
						<option value="credit">Credit</option>
						<option value="cash">Cash</option>
						<option value="product">Product</option>
					</Select>
				</Field>
				<div className="relative w-64">
					<Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-zinc-400" />
					<Input
						type="text"
						placeholder="Search ID / reason..."
						className="pl-9"
						value={search}
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setSearch(e.target.value); setOffset(0); }}
					/>
				</div>
			</div>

			{error && (
				<div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
					{error}
				</div>
			)}

			{loading ? (
				<Text className="mt-6">Loading transactions...</Text>
			) : transactions.length === 0 ? (
				<Text className="mt-6">No transactions found.</Text>
			) : (
				<>
					<Table className="mt-6">
						<TableHead>
							<TableRow>
								<TableHeader>Type</TableHeader>
								<TableHeader>Currency</TableHeader>
								<TableHeader>Amount</TableHeader>
								<TableHeader>User</TableHeader>
								<TableHeader>Order</TableHeader>
								<TableHeader>Code</TableHeader>
								<TableHeader>Reason</TableHeader>
								<TableHeader>Created</TableHeader>
							</TableRow>
						</TableHead>
						<TableBody>
							{transactions.map((t) => (
								<TableRow key={t.id}>
									<TableCell>
										<Badge color="zinc">
											{ENTRY_TYPE_LABELS[t.entry_type] || t.entry_type}
										</Badge>
									</TableCell>
									<TableCell className="uppercase">{t.currency}</TableCell>
									<TableCell
										className={
											t.amount < 0
												? 'font-medium text-red-600 dark:text-red-400'
												: 'font-medium text-green-600 dark:text-green-400'
										}
									>
										{formatAmount(t.amount, t.currency)}
									</TableCell>
									<TableCell>
										<ResourceLink
											id={t.user_id}
											href={`/users/${t.user_id}`}
											name={t.users?.display_name || t.users?.phone_number}
										/>
									</TableCell>
									<TableCell>
										{t.order_id ? (
											<ResourceLink
												id={t.order_id}
												href={`/orders/${t.order_id}`}
											/>
										) : (
											'—'
										)}
									</TableCell>
									<TableCell>
										{t.code_id ? (
											<ResourceLink
												id={t.code_id}
												href={`/codes/${t.code_id}`}
											/>
										) : (
											'—'
										)}
									</TableCell>
									<TableCell className="max-w-[200px] truncate text-zinc-500">
										{t.reason || '—'}
									</TableCell>
									<TableCell className="whitespace-nowrap text-zinc-500">
										{formatDate(t.created_at)}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>

					{totalPages > 1 && (
						<div className="mt-4 flex items-center justify-between">
							<Text>
								Page {currentPage} of {totalPages}
								{count != null && ` (${count} transactions)`}
							</Text>
							<div className="flex gap-2">
								<Button
									plain
									disabled={offset === 0}
									onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
								>
									Previous
								</Button>
								<Button
									plain
									disabled={currentPage >= totalPages}
									onClick={() => setOffset(offset + PAGE_SIZE)}
								>
									Next
								</Button>
							</div>
						</div>
					)}
				</>
			)}
		</div>
	);
};

export default LedgerPage;
